import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { MonEventService } from '@monsido/services/mon-event/mon-event.service';
import { AssignedProfilesRepoService } from '@client/app/services/api/page-performance/assigned-profile-repo/assigned-profiles-repo.service';
import { SessionService } from '@monsido/core/session/session.service';
import { PagePerformanceProfileObsService } from '@client/app/modules/page-performance/services/profile-obs/profile-obs.service';

@Component({
    selector: 'page-performance-profile-dropdown',
    standalone: false,
    template: `
        <div class="profile-select-dropdown" [class.no-selector-arrow]="profiles.length < 2">
            <label for="pagedetails_performance_profiles" class="sr-only">Profiles</label>
            <angular-selector monId="pagedetails_performance_profiles"
                    *ngIf="profileId > 0"
                    name="performance_profile"
                    [options]="profiles"
                    valueAttr="id"
                    [disabled]="profiles.length < 2"
                    [debounce]="300"
                    [dropdownItemTemplate]="'app/modules/page-performance/components/profile-dropdown/profile-dropdown-templates/profile-select-item.html'"
                    [viewItemTemplate]="'app/modules/page-performance/components/profile-dropdown/profile-dropdown-templates/profile-select.html'"
                    [removeButton]="false"
                    (change)="onChangeProfile($event)"
                    [(ngModel)]="profileId"
                    [hideDropdownIcon]="true"
                    [disableSearch]="true">
            </angular-selector>
        </div>
    `,
})
export class PagePerformanceProfileDropdownComponent implements OnInit, OnDestroy {
    @Input() page: any;

    profileId: any = null;
    accountProfiles: any[] = [];
    profiles: any[] = [];
    assignedProfiles: any[] = [];
    refreshProfilesId: any;

    constructor (
        private monEventService: MonEventService,
        private assignedProfileRepoService: AssignedProfilesRepoService,
        private sessionService: SessionService,
        private pagePerformanceProfileObsService: PagePerformanceProfileObsService,
    ) {}

    ngOnInit () {
        this.profileId = null;
        this.accountProfiles = [];
        this.profiles = [];
        this.assignedProfiles = [];
        this.setupAssignedProfiles();
        this.setupListeners();
    }

    ngOnDestroy () {
        this.monEventService.remove(this.refreshProfilesId);
    }

    onChangeProfile (event: any) {
        const newValue = event.newValue || event;
        if (newValue === null || typeof newValue !== 'object' || newValue.id === this.profileId) {
            return;
        }
        this.runSelectProfileEvent(newValue);
    }

    // PROTECTED

    private setupListeners () {
        this.pagePerformanceProfileObsService.profilesObs.subscribe((profiles: any) => {
            this.accountProfiles = profiles;
        });
        this.refreshProfilesId = this.monEventService.addListener('pagePerformanceRefreshProfiles' as any, () => this.setupAssignedProfiles());
    }

    private runSelectProfileEvent (profile: any) {
        this.monEventService.run('pagePerformanceSelectProfile', profile);
    }

    private setupAssignedProfiles () {
        // Get Assigned Profiles and Profiles sequentially
        this.monEventService.run('pagePerformanceBeforeSelectProfile');
        const chain = Promise.resolve();
        chain
            .then(() => this.getAssignedProfiles())
            .catch(() => {})
            .then(() => {
                // Merge assigned profile into each profile
                this.profiles = this.getProfilesForPage();
                this.profiles.forEach((profileItem: any) => {
                    profileItem.assignedProfile = this.assignedProfiles.find((assignedProfile: any) => {
                        return this.page.id === assignedProfile.page.id && assignedProfile.profile_ids.indexOf(profileItem.id) > -1;
                    });
                });
                const selectedProfile = this.profiles.find((profileItem: any) => {
                    return profileItem.assignedProfile.page.id === this.page.id && profileItem.assignedProfile.default_profile_id === profileItem.id;
                });
                if (selectedProfile) {
                    this.profileId = selectedProfile.id;
                } else if (this.profiles.length > 0) {
                    this.profileId = this.profiles[0].id;
                }

                if (this.profileId > 0) {
                    this.runSelectProfileEvent(selectedProfile || this.profiles[0]);
                }
            })
            .finally(() => {
                this.monEventService.run('pagePerformanceAfterSelectProfile');
            });
    }

    private getProfilesForPage () {
        const profileIds = this.assignedProfiles.flatMap((item: any) => item.profile_ids);
        return this.accountProfiles.filter((profile: any) => {
            return profileIds.indexOf(profile.id) > -1;
        });
    }

    private getAssignedProfiles () {
        const params = {
            page_id: this.page.id,
            domain_id: this.sessionService.getDomainId(),
        };
        return this.assignedProfileRepoService.getAll(params).then((assignedProfiles: any) => {
            this.assignedProfiles = assignedProfiles;
        }, () => {});
    }
}
